import { heroData, missionVision, images } from "../../data/mock";
import { Button } from "../ui/button";
import { ArrowDown } from "lucide-react";

export const HeroSection = () => {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={images.prayer}
          alt="Comunidad en oración"
          className="w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-950/90 via-brand-900/75 to-brand-950/92" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-950/40 to-transparent" />
      </div>

      {/* Vivid Decorative Elements */}
      <div className="absolute top-32 right-32 w-80 h-80 bg-brand-light/10 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-32 left-16 w-96 h-96 bg-brand-500/8 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute top-1/2 right-1/4 w-4 h-4 bg-brand-light/40 rounded-full animate-float" />
      <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-brand-light/30 rounded-full animate-float-slow" />
      <div className="absolute bottom-1/3 left-1/3 w-3 h-3 bg-white/20 rounded-full animate-float" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            {/* Badge - no emoji */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-light/10 backdrop-blur-md border border-brand-light/20 mb-8 animate-fade-in animate-border-glow">
              <div className="w-2 h-2 rounded-full bg-brand-light animate-pulse" />
              <span className="text-brand-200 text-sm font-semibold tracking-wide">
                #1 Plataforma para Ministerios Cristianos
              </span>
            </div>

            {/* Title */}
            <h1 className="hero-title text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-[0.95] animate-fade-in delay-100">
              TU IGLESIA
              <span className="block text-brand-light">APP</span>
            </h1>

            {/* Tagline */}
            <p className="brand-heading text-xl md:text-2xl text-brand-300/90 font-medium mb-6 tracking-wide animate-fade-in delay-200">
              {heroData.tagline}
            </p>

            {/* Description */}
            <p className="text-lg text-white/70 mb-10 max-w-xl leading-relaxed animate-fade-in delay-300">
              {heroData.description}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-16 animate-fade-in delay-400">
              <Button
                onClick={() => scrollTo("#registro")}
                className="bg-brand-700 hover:bg-brand-800 text-white px-8 py-6 text-lg rounded-2xl shadow-2xl shadow-brand-light/15 transition-all duration-300 hover:scale-105 hover:shadow-brand-light/25 font-semibold animate-pulse-glow"
              >
                {heroData.ctaText}
              </Button>
              <Button
                onClick={() => scrollTo("#features")}
                variant="outline"
                className="border-brand-light/30 text-white hover:bg-brand-light/10 px-8 py-6 text-lg rounded-2xl backdrop-blur-sm transition-all duration-300 hover:scale-105 font-medium"
              >
                {heroData.secondaryCta}
              </Button>
            </div>

            {/* Mission & Vision Cards */}
            <div className="grid sm:grid-cols-2 gap-4 animate-fade-in delay-500">
              <div className="p-6 rounded-2xl bg-white/8 backdrop-blur-md border border-brand-light/10 hover:bg-white/12 hover:border-brand-light/25 transition-all duration-500 group">
                <h3 className="text-brand-light font-bold text-sm uppercase tracking-wider mb-3 brand-heading">
                  {missionVision.mission.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {missionVision.mission.text}
                </p>
              </div>
              <div className="p-6 rounded-2xl bg-white/8 backdrop-blur-md border border-brand-light/10 hover:bg-white/12 hover:border-brand-light/25 transition-all duration-500 group">
                <h3 className="text-brand-light font-bold text-sm uppercase tracking-wider mb-3 brand-heading">
                  {missionVision.vision.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {missionVision.vision.text}
                </p>
              </div>
            </div>

            {/* Verse */}
            <div className="mt-8 flex items-start gap-3">
              <div className="w-1 h-12 bg-brand-light/50 rounded-full mt-1 flex-shrink-0" />
              <div>
                <p className="text-white/45 italic text-sm leading-relaxed">
                  {missionVision.verse.text}
                </p>
                <p className="text-brand-light/50 text-xs mt-1 font-semibold">
                  {missionVision.verse.reference}
                </p>
              </div>
            </div>
          </div>

          {/* Logo on the right - floating animation */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-brand-light/5 rounded-full blur-3xl scale-110" />
              <img
                src={images.logoWhite}
                alt="Tu Iglesia App"
                className="w-72 h-auto opacity-15 hover:opacity-25 transition-opacity duration-700 animate-float-slow relative z-10"
              />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <button
            onClick={() => scrollTo("#stats")}
            className="p-3 rounded-full bg-brand-light/10 backdrop-blur-sm border border-brand-light/20 hover:bg-brand-light/20 transition-colors"
          >
            <ArrowDown className="w-5 h-5 text-brand-light/60" />
          </button>
        </div>
      </div>
    </section>
  );
};
